<template>
    <modal :name="modalName" :adaptive="true" :focusTrap="true" :width="800" height="auto" @opened="$refs.heading.focus()" @before-close="$emit('closed')">
        <div class="envelope-picker p-6 bg-white">
            <h2 ref="heading" tabindex="-1" class="picker-heading text-lg font-semibold pb-4 mb-4">Select documents for this envelope</h2>
            <div class="picker-panel">
                <header class="picker-header">
                    <h3 class="text-base font-semibold">Transaction Documents</h3>
                    <label class="picker-search"><span class="sr-only">Search transaction documents</span><search-icon size="16" aria-hidden="true" /><input v-model="search" type="search" /></label>
                </header>
                <div class="picker-list">
                    <library-folder-list :folders="folderTree" :documents="rootDocuments" :open-ids="openFolders" :searching="!!search.trim()" selection-mode
                        :selected-keys="selectedKeys" @toggle="toggleFolder" @select-document="toggleDocument" />
                    <p v-if="!folderTree.length && !rootDocuments.length" class="text-sm text-gray-500 text-center px-4 py-6">{{ search.trim() ? 'No matching documents or folders.' : 'No transaction documents available.' }}</p>
                </div>
                <footer class="text-xs text-gray-500 px-3 py-3 border-t border-gray-200">Choose documents from this transaction to add to the envelope.</footer>
            </div>
            <div class="flex items-center justify-between gap-3 mt-6">
                <span class="text-sm text-gray-500" role="status">{{ selected_documents.length }} selected</span>
                <div class="flex gap-3"><button type="button" class="picker-button" @click="$modal.hide(modalName)">Cancel</button><button type="button" class="picker-button picker-primary" @click="done">Save</button></div>
            </div>
        </div>
    </modal>
</template>
<script>
import { SearchIcon } from 'vue-feather-icons';
import LibraryFolderList from '../../../broker/library/library-folder-list.vue';

export default {
    components: { SearchIcon, LibraryFolderList },
    props: {
        show: {}, property_id: { required: true }, property: {},
        envelope_documents: { default: () => [] }, documents: { default: () => [] },
        templates: { default: () => [] }, document_folders: { default: () => [] },
    },
    data() { return { selected_documents: [], search: '', openFolders: [] }; },
    computed: {
        modalName() { return `envelope-document-select-${this._uid}`; },
        selectedKeys() { return this.selected_documents.map(this.documentKey); },
        availableDocuments() {
            const documents = this.documents.map(item => ({ ...item, type: 'document' }));
            const templates = this.templates.map(item => ({ ...item, type: 'template', display_filename: item.display_name || item.name,
                folder_id: this.document_folders.find(folder => folder.default_folder_id === item.folder_id)?.id ?? null,
                size_for_humans: 'Template' }));
            return [...documents, ...templates];
        },
        rootDocuments() {
            return this.sort(this.availableDocuments.filter(item => !this.document_folders.some(folder => folder.id === item.folder_id) && this.matches(item.display_filename)));
        },
        folderTree() {
            const query = this.search.trim().toLowerCase();
            const build = (parentId, ancestorMatch = false, ancestors = []) => this.sort(this.document_folders.filter(folder =>
                parentId === null ? !this.document_folders.some(parent => parent.id === folder.parent_id) : folder.parent_id === parentId))
                .filter(folder => !ancestors.includes(folder.id)).map(folder => {
                    const matched = ancestorMatch || (query && folder.name.toLowerCase().includes(query));
                    const children = build(folder.id, matched, [...ancestors, folder.id]);
                    const documents = this.sort(this.availableDocuments.filter(item => item.folder_id === folder.id && (matched || this.matches(item.display_filename))));
                    return { ...folder, children, documents, documentCount: documents.length + children.reduce((count, child) => count + child.documentCount, 0), matched };
                }).filter(folder => !query || folder.matched || folder.children.length || folder.documents.length);
            return build(null);
        },
    },
    methods: {
        documentKey(item) { return `${item.type || 'document'}:${item.id}`; },
        sort(items) { return [...items].sort((a, b) => (a.order || 0) - (b.order || 0) || (a.name || a.display_filename || '').localeCompare(b.name || b.display_filename || '')); },
        matches(name) { return (name || '').toLowerCase().includes(this.search.trim().toLowerCase()); },
        toggleFolder(folder) { this.openFolders = this.openFolders.includes(folder.id) ? this.openFolders.filter(id => id !== folder.id) : [...this.openFolders, folder.id]; },
        toggleDocument(document) {
            const key = this.documentKey(document);
            if (this.selectedKeys.includes(key)) this.selected_documents = this.selected_documents.filter(item => this.documentKey(item) !== key);
            else this.selected_documents.push({ ...document });
        },
        beforeOpen() {
            this.selected_documents = (this.envelope_documents || []).map(item => ({ ...item }));
            this.search = '';
            this.openFolders = this.document_folders.map(folder => folder.id);
        },
        done() { this.$emit('selected', this.selected_documents.map(item => ({ ...item }))); this.$modal.hide(this.modalName); },
    },
    watch: {
        show(value) { if (value) { this.beforeOpen(); this.$modal.show(this.modalName); } else this.$modal.hide(this.modalName); },
    },
};
</script>
<style scoped>
.envelope-picker { display: flex; flex-direction: column; max-height: 85vh; }
.picker-heading:focus { outline: none; }
.envelope-picker > h2 { flex-shrink: 0; }
.picker-panel { display: flex; flex-direction: column; min-height: 0; height: 26rem; border: 1px solid #d1d5db; border-radius: 0.5rem; overflow: hidden; }
.picker-header { display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; gap: 0.75rem; padding: 0.5rem 0.75rem; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
.picker-header h3 { margin: 0; }
.picker-search { position: relative; width: 14rem; min-width: 0; }
.picker-search svg { position: absolute; left: 0.65rem; top: 50%; transform: translateY(-50%); color: #9ca3af; }
.picker-search input { width: 100%; border: 1px solid #e5e7eb; border-radius: 9999px; background: #fdfdfd; padding: 0.35rem 0.75rem 0.35rem 2rem; font-size: 0.8125rem; }
.picker-list { flex: 1; min-height: 0; overflow-y: auto; --library-folder-padding: 0.15rem 0.75rem; --library-folder-toggle-padding: 0.25rem 0; --library-folder-font-size: 0.875rem; --library-document-padding: 0.2rem 0.75rem; --library-document-min-height: 2.125rem; }
.picker-panel footer { flex-shrink: 0; }
.picker-button { display: inline-flex; align-items: center; justify-content: center; border: 1px solid #d1d5db; border-radius: 0.375rem; padding: 0.55rem 0.9rem; font-size: 0.875rem; background: white; color: #374151; }
.picker-button:hover { color: #2563eb; border-color: #2563eb; }
.picker-primary { background: #2563eb; border-color: #2563eb; color: white; }
.picker-primary:hover { background: #1d4ed8; color: white; }
</style>
